import React, { useState, useEffect } from 'react';
import { Workout, Exercise, WorkoutLogSet } from '../../types/database.types';
import { gymAudio } from '../../lib/audioTimer';
import confetti from 'canvas-confetti';
import { 
  Play, 
  Pause, 
  Check, 
  Clock, 
  Dumbbell, 
  Flame, 
  Plus, 
  Minus, 
  ChevronRight, 
  SkipForward, 
  Activity, 
  HeartPulse, 
  Sparkles,
  Info,
  X,
  Lock,
  Zap
} from 'lucide-react';

type SessionPhase = 'prep' | 'executing' | 'rest' | 'cardio' | 'summary';

interface GuidedWorkoutSessionProps {
  workout: Workout;
  onExit: () => void;
  onFinishWorkout: (workoutName: string, durationSecs: number, totalVolumeKg: number) => void;
}

export const GuidedWorkoutSession: React.FC<GuidedWorkoutSessionProps> = ({
  workout,
  onExit,
  onFinishWorkout,
}) => {
  const exercises = workout.exercises || [];
  const [currentExIndex, setCurrentExIndex] = useState(0);
  const [currentSetNumber, setCurrentSetNumber] = useState(1);
  const [phase, setPhase] = useState<SessionPhase>('prep');

  // Carga e repetições da série ativa
  const currentExercise = exercises[currentExIndex];
  const [activeWeight, setActiveWeight] = useState<number>(currentExercise?.default_weight_kg || 24);
  const [activeReps, setActiveReps] = useState<number>(currentExercise?.target_reps_min || 10);

  // Timers
  const [prepTimeLeft, setPrepTimeLeft] = useState<number>(15); // 15s de tolerância para chegar no equipamento
  const [executingSeconds, setExecutingSeconds] = useState<number>(0); // cronômetro da série
  const [restTimeLeft, setRestTimeLeft] = useState<number>(currentExercise?.default_rest_seconds || 90);
  const [totalRestSeconds, setTotalRestSeconds] = useState<number>(currentExercise?.default_rest_seconds || 90);
  const [cardioTimeLeft, setCardioTimeLeft] = useState<number>((workout.cardio_duration_min || 20) * 60);

  // Estatísticas gerais
  const [totalSessionSeconds, setTotalSessionSeconds] = useState(0);
  const [totalVolumeKg, setTotalVolumeKg] = useState(0);
  const [completedSetsHistory, setCompletedSetsHistory] = useState<WorkoutLogSet[]>([]);
  const [showDemo, setShowDemo] = useState(false);

  // Atualiza peso padrão ao trocar de exercício
  useEffect(() => {
    if (currentExercise) {
      setActiveWeight(currentExercise.default_weight_kg || 24);
      setActiveReps(currentExercise.target_reps_min || 10);
      setShowDemo(false);
    }
  }, [currentExIndex]);

  // Cronômetro global da sessão
  useEffect(() => {
    const timer = setInterval(() => {
      setTotalSessionSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 1. FASE 'prep': Tolerância de 15 segundos para chegar no aparelho
  useEffect(() => {
    if (phase !== 'prep') return;

    if (prepTimeLeft <= 0) {
      startExecution();
      return;
    }

    const timer = setInterval(() => {
      setPrepTimeLeft((prev) => {
        if (prev <= 1) {
          startExecution();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [phase, prepTimeLeft]);

  // 2. FASE 'executing': Cronômetro da série com trava de 15 segundos
  useEffect(() => {
    if (phase !== 'executing') return;

    const timer = setInterval(() => {
      setExecutingSeconds((s) => {
        const next = s + 1;
        // No segundo 15 exato, vibra para alertar que o botão foi liberado!
        if (next === 15) {
          if ('vibrate' in navigator) navigator.vibrate([100, 50, 100]);
          gymAudio.playCountdownTick();
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [phase]);

  // 3. FASE 'rest': Cronômetro de Descanso Automático com Haptic Feedback
  useEffect(() => {
    if (phase !== 'rest') return;

    const timer = setInterval(() => {
      setRestTimeLeft((prev) => {
        if (prev <= 1) {
          gymAudio.playRestFinishedAlarm();
          if ('vibrate' in navigator) {
            navigator.vibrate([250, 100, 250, 100, 450]);
          }
          setTimeout(() => {
            advanceToNextSetOrExercise();
          }, 350);
          return 0;
        }

        const next = prev - 1;
        if (next === 3 || next === 2) {
          gymAudio.playCountdownTick();
          if ('vibrate' in navigator) {
            navigator.vibrate(80);
          }
        } else if (next === 1) {
          gymAudio.playCountdownTick();
          if ('vibrate' in navigator) {
            navigator.vibrate(120);
          }
        }

        return next;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [phase, currentSetNumber, currentExIndex]);

  // 4. FASE 'cardio': Cronômetro regressivo do cardio pós-treino
  useEffect(() => {
    if (phase !== 'cardio') return;

    if (cardioTimeLeft <= 0) {
      finishAllWorkout();
      return;
    }

    const timer = setInterval(() => {
      setCardioTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [phase, cardioTimeLeft]);

  // Transições de Estado
  const startExecution = () => {
    setPhase('executing');
    setExecutingSeconds(0);
    if ('vibrate' in navigator) navigator.vibrate(80);
  };

  const handleFinishSet = () => {
    // Computa volume da série
    const setVolume = activeWeight * activeReps;
    setTotalVolumeKg((v) => v + setVolume);

    const log: WorkoutLogSet = {
      id: `set-${Date.now()}`,
      session_id: 'guided-session',
      exercise_id: currentExercise.id,
      set_number: currentSetNumber,
      weight_kg: activeWeight,
      reps_performed: activeReps,
      completed: true,
    };
    setCompletedSetsHistory((prev) => [...prev, log]);

    // Entra no descanso ativo automático
    const restSecs = currentExercise.default_rest_seconds || 90;
    setTotalRestSeconds(restSecs);
    setRestTimeLeft(restSecs);
    setPhase('rest');
    gymAudio.playCountdownTick();
    if ('vibrate' in navigator) navigator.vibrate([100, 50, 100]);
  };

  const advanceToNextSetOrExercise = () => {
    const totalSets = currentExercise.default_sets || 4;

    if (currentSetNumber < totalSets) {
      // Avança para a próxima série do mesmo exercício
      setCurrentSetNumber((s) => s + 1);
      setPhase('prep');
      setPrepTimeLeft(15);
    } else {
      // Concluiu todas as séries deste exercício!
      if (currentExIndex < exercises.length - 1) {
        // Passa para o próximo exercício
        setCurrentExIndex((idx) => idx + 1);
        setCurrentSetNumber(1);
        setPhase('prep');
        setPrepTimeLeft(20); // 20s de tolerância para trocar de aparelho
      } else {
        // Concluiu todos os exercícios! Avança para o CARDIO PÓS-TREINO
        setPhase('cardio');
        setCardioTimeLeft((workout.cardio_duration_min || 20) * 60);
      }
    }
  };

  const finishAllWorkout = () => {
    try {
      confetti({
        particleCount: 120,
        spread: 120,
        origin: { y: 0.5 },
        colors: ['#a3e635', '#00f0ff', '#ffffff', '#8b5cf6'],
      });
    } catch (e) {}

    setPhase('summary');
  };

  const formatSeconds = (totalSecs: number) => {
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Se estiver no resumo final
  if (phase === 'summary') {
    return (
      <div 
        className="space-y-3 pb-8 max-w-xl mx-auto text-center animate-fadeIn select-none text-slate-900 dark:text-slate-100"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 10px)' }}
      >
        <div className="flex justify-end py-1">
          <button 
            onClick={onExit} 
            className="w-9 h-9 rounded-full bg-white/90 dark:bg-[#1C1C1E] border border-black/[0.08] dark:border-white/[0.08] text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white flex items-center justify-center apple-press shadow-sm cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 rounded-[28px] bg-white/95 dark:bg-[#1C1C1E] border border-black/[0.06] dark:border-white/[0.08] space-y-4 shadow-md">
          <div className="w-20 h-20 rounded-full bg-[#30D158]/15 text-[#30D158] border border-[#30D158]/30 flex items-center justify-center mx-auto shadow-[0_0_30px_rgba(48,209,88,0.25)] animate-bounce">
            <Check className="w-10 h-10 stroke-[3]" />
          </div>

          <div className="space-y-1">
            <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">Treino Concluído</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
              {workout.name} executado com disciplina exemplar e cardio Zona 2 finalizado.
            </p>
          </div>

          {/* Cards de Métricas */}
          <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto text-left pt-2">
            <div className="p-4 rounded-[22px] bg-slate-50 dark:bg-black/50 border border-black/[0.06] dark:border-white/[0.08] shadow-sm">
              <span className="text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400 block">Volume Total</span>
              <div className="text-2xl font-extrabold tabular-nums text-[#30D158] mt-1">
                {totalVolumeKg.toLocaleString('pt-BR')} <span className="text-xs font-normal text-slate-400">kg</span>
              </div>
            </div>

            <div className="p-4 rounded-[22px] bg-slate-50 dark:bg-black/50 border border-black/[0.06] dark:border-white/[0.08] shadow-sm">
              <span className="text-[10px] font-semibold uppercase text-slate-500 dark:text-slate-400 block">Tempo Total</span>
              <div className="text-2xl font-extrabold tabular-nums text-[#0A84FF] mt-1">
                {formatSeconds(totalSessionSeconds)}
              </div>
            </div>
          </div>

          <button
            onClick={() => {
              onFinishWorkout(workout.name, totalSessionSeconds, totalVolumeKg);
              onExit();
            }}
            className="w-full py-5 rounded-2xl bg-[#30D158] hover:bg-[#28C846] text-black font-black text-lg uppercase tracking-wider shadow-lg active:scale-95 transition cursor-pointer"
          >
            Salvar & Concluir
          </button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // FASE DO CARDIO PÓS-TREINO (Apple Fitness Style)
  // -------------------------------------------------------------
  if (phase === 'cardio') {
    const cardioMins = Math.floor(cardioTimeLeft / 60);
    const cardioSecs = cardioTimeLeft % 60;

    return (
      <div 
        className="space-y-4 pb-8 max-w-xl mx-auto select-none animate-fadeIn"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 10px)' }}
      >
        {/* Dynamic Island HUD */}
        <div className="flex items-center justify-between py-1">
          <div className="flex items-center gap-2 bg-white/90 dark:bg-[#1C1C1E] border border-black/[0.08] dark:border-white/[0.08] px-3.5 py-1.5 rounded-full shadow-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-[#FF453A] animate-ping" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#FF453A]">
              Cardio Pós-Treino
            </span>
          </div>
          <button 
            onClick={finishAllWorkout} 
            className="text-xs font-bold text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white px-3.5 py-1.5 rounded-full bg-white/90 dark:bg-[#1C1C1E] border border-black/[0.08] dark:border-white/[0.08] apple-press shadow-sm cursor-pointer"
          >
            Finalizar Já
          </button>
        </div>

        {/* Display do Cardio */}
        <div className="p-6 rounded-[28px] bg-white/95 dark:bg-[#1C1C1E] border border-black/[0.06] dark:border-white/[0.08] text-center space-y-4 shadow-md w-full">
          <div className="w-16 h-16 rounded-full bg-[#FF453A]/15 text-[#FF453A] border border-[#FF453A]/30 flex items-center justify-center mx-auto shadow-sm">
            <HeartPulse className="w-8 h-8 animate-pulse" />
          </div>

          <div>
            <h2 className="text-xl font-black text-slate-900 dark:text-white">Zona Lipolítica (Zona 2)</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto mt-1 leading-relaxed">
              {workout.cardio_instructions || 'Esteira inclinada (10% a 12%) a 5.0 km/h. Mantenha 120-135 BPM.'}
            </p>
          </div>

          <div className="text-7xl font-extrabold tabular-nums tracking-tighter text-slate-900 dark:text-white py-1">
            {cardioMins}:{cardioSecs < 10 ? `0${cardioSecs}` : cardioSecs}
          </div>

          <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-[#FF453A]/20 text-[#FF453A] border border-[#FF453A]/30 inline-block">
            QUEIMA DE GORDURA ATIVA
          </span>

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => setCardioTimeLeft((t) => t + 300)}
              className="flex-1 py-3 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-[#2C2C2E] dark:hover:bg-[#3A3A3C] border border-black/[0.06] dark:border-white/[0.08] text-xs font-bold text-slate-800 dark:text-slate-200 apple-press shadow-sm cursor-pointer"
            >
              +5 Minutos
            </button>
            <button
              onClick={() => setCardioTimeLeft((t) => Math.max(0, t - 300))}
              className="flex-1 py-3 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-[#2C2C2E] dark:hover:bg-[#3A3A3C] border border-black/[0.06] dark:border-white/[0.08] text-xs font-bold text-slate-800 dark:text-slate-200 apple-press shadow-sm cursor-pointer"
            >
              -5 Minutos
            </button>
          </div>

          <button
            onClick={finishAllWorkout}
            className="w-full py-5 rounded-2xl bg-[#FF453A] hover:bg-[#E0382E] text-white font-black text-base uppercase tracking-wider shadow-lg active:scale-95 transition cursor-pointer"
          >
            Concluir Cardio & Finalizar Treino
          </button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // FASE DO DESCANSO ATIVO (Apple Fitness Rest)
  // -------------------------------------------------------------
  if (phase === 'rest') {
    const restMins = Math.floor(restTimeLeft / 60);
    const restSecs = restTimeLeft % 60;
    const baseTotal = totalRestSeconds > 0 ? totalRestSeconds : 90;
    const progressPercent = Math.min(100, Math.max(0, ((baseTotal - restTimeLeft) / baseTotal) * 100));
    const ringRadius = 84;
    const ringCircumference = 2 * Math.PI * ringRadius;
    const strokeDashoffset = ringCircumference * (1 - progressPercent / 100);
    const isImminent = restTimeLeft <= 3 && restTimeLeft > 0;

    return (
      <div 
        className="space-y-4 pb-8 max-w-xl mx-auto select-none animate-fadeIn transition-colors duration-500"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 10px)' }}
      >
        <div className="flex items-center justify-between py-1">
          <div className="flex items-center gap-2 bg-white/90 dark:bg-[#1C1C1E] border border-black/[0.08] dark:border-white/[0.08] px-3.5 py-1.5 rounded-full shadow-sm">
            <span className="w-2.5 h-2.5 rounded-full bg-[#30D158] animate-pulse" />
            <span className="text-[11px] font-bold tracking-wider uppercase text-[#30D158]">
              Descanso Automático
            </span>
          </div>
          <button 
            onClick={advanceToNextSetOrExercise} 
            className="text-xs font-bold text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white px-3.5 py-1.5 rounded-full bg-white/90 dark:bg-[#1C1C1E] border border-black/[0.08] dark:border-white/[0.08] apple-press shadow-sm cursor-pointer"
          >
            Pular
          </button>
        </div>

        <div className="p-6 rounded-[28px] bg-white/95 dark:bg-[#1C1C1E] border border-black/[0.06] dark:border-white/[0.08] text-center space-y-4 shadow-md w-full relative overflow-hidden">
          {/* Identificação do Exercício & Próxima Série */}
          <div className="space-y-1">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
              {currentExercise.name}
            </span>
            <h2 className="text-lg font-black text-slate-900 dark:text-white">
              Próxima: Série #{currentSetNumber < currentExercise.default_sets ? currentSetNumber + 1 : 1}
            </h2>
          </div>

          {/* Anel SVG de Descanso Estilo Apple Fitness */}
          <div className="relative w-56 h-56 mx-auto flex items-center justify-center my-1">
            <svg className="w-full h-full transform -rotate-90 drop-shadow-[0_0_18px_rgba(48,209,88,0.25)]" viewBox="0 0 200 200">
              <defs>
                <linearGradient id="restRingGreen" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#30D158" />
                  <stop offset="100%" stopColor="#30D6FB" />
                </linearGradient>
                <linearGradient id="restRingImminent" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FF9F0A" />
                  <stop offset="100%" stopColor="#FF453A" />
                </linearGradient>
              </defs>

              <circle
                cx="100"
                cy="100"
                r={ringRadius}
                stroke="rgba(120, 120, 128, 0.15)"
                strokeWidth="12"
                fill="transparent"
              />

              <circle
                cx="100"
                cy="100"
                r={ringRadius}
                stroke={isImminent ? "url(#restRingImminent)" : "url(#restRingGreen)"}
                strokeWidth="12"
                strokeDasharray={ringCircumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                fill="transparent"
                className="transition-all duration-500 ease-out"
              />
            </svg>

            {/* Numerais Centrais */}
            <div className="absolute flex flex-col items-center justify-center">
              {isImminent ? (
                <div className="text-7xl font-black text-[#FF9F0A] tabular-nums animate-pulse drop-shadow-[0_0_15px_rgba(255,159,10,0.5)]">
                  {restTimeLeft}
                </div>
              ) : (
                <div className="text-6xl font-extrabold tabular-nums tracking-tighter text-slate-900 dark:text-white">
                  {restMins}:{restSecs < 10 ? `0${restSecs}` : restSecs}
                </div>
              )}

              <span className={`text-[10px] font-extrabold tracking-wider uppercase mt-1 px-2.5 py-0.5 rounded-full ${
                isImminent 
                  ? 'bg-[#FF9F0A]/20 text-[#FF9F0A] border border-[#FF9F0A]/30' 
                  : 'bg-[#30D158]/15 text-[#30D158] border border-[#30D158]/25'
              }`}>
                {isImminent ? 'Prepare-se!' : 'Recuperando ATP'}
              </span>
            </div>
          </div>

          {/* Badge Indicativo de Haptic Feedback e Avanço Automático */}
          <div className="flex items-center justify-center gap-1.5 px-3 py-1 rounded-full bg-black/[0.04] dark:bg-white/[0.06] border border-black/[0.06] dark:border-white/[0.08] text-[11px] font-bold text-slate-600 dark:text-white/70 w-fit mx-auto">
            <Zap className="w-3 h-3 text-[#30D158] fill-[#30D158]" />
            <span>Avanço Automático • Haptic Ativo</span>
          </div>

          {/* BOTÃO COMEÇAR PRÓXIMA SÉRIE AGORA */}
          <button
            onClick={advanceToNextSetOrExercise}
            className="w-full py-5 px-8 rounded-2xl bg-[#30D158] hover:bg-[#28C846] text-black font-black text-2xl uppercase tracking-wider shadow-lg active:scale-95 transition-all flex items-center justify-center gap-3 cursor-pointer"
          >
            <Play className="w-7 h-7 fill-black" />
            <span>Começar Próxima Série</span>
          </button>

          {/* Controles de Ajuste de Tempo */}
          <div className="flex items-center justify-center gap-2 w-full pt-1">
            <button
              onClick={() => {
                setRestTimeLeft((t) => t + 30);
                setTotalRestSeconds((t) => Math.max(t, t + 30));
                if ('vibrate' in navigator) navigator.vibrate(40);
              }}
              className="flex-1 py-3 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-[#2C2C2E] dark:hover:bg-[#3A3A3C] border border-black/[0.06] dark:border-white/[0.08] text-xs tabular-nums font-bold text-slate-800 dark:text-slate-200 active:scale-95 cursor-pointer apple-press shadow-sm"
            >
              +30s Descanso
            </button>
            <button
              onClick={() => {
                setRestTimeLeft((t) => Math.max(0, t - 15));
                if ('vibrate' in navigator) navigator.vibrate(40);
              }}
              className="flex-1 py-3 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-[#2C2C2E] dark:hover:bg-[#3A3A3C] border border-black/[0.06] dark:border-white/[0.08] text-xs tabular-nums font-bold text-slate-800 dark:text-slate-200 active:scale-95 cursor-pointer apple-press shadow-sm"
            >
              -15s Descanso
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // FASE PRINCIPAL: 'prep' OU 'executing' (Apple Dynamic Island Live Session)
  // -------------------------------------------------------------
  const isExecuting = phase === 'executing';
  const isLocked = isExecuting && executingSeconds < 15; // Trava de 15 segundos
  const lockProgress = Math.min(100, Math.round((executingSeconds / 15) * 100));

  return (
    <div 
      className="space-y-3 pb-8 max-w-xl mx-auto animate-fadeIn select-none"
      style={{ paddingTop: 'calc(env(safe-area-inset-top) + 10px)' }}
    >
      {/* 1. Dynamic Island HUD / Header Superior */}
      <div className="flex items-center justify-between py-1">
        <div className="flex items-center gap-2 bg-white/90 dark:bg-[#1C1C1E] border border-black/[0.08] dark:border-white/[0.08] px-3.5 py-1.5 rounded-full shadow-sm">
          <span className={`w-2.5 h-2.5 rounded-full ${isExecuting ? 'bg-[#30D158] animate-ping' : 'bg-[#FF9F0A]'}`} />
          <span className={`text-[11px] font-bold uppercase tracking-wider ${isExecuting ? 'text-[#30D158]' : 'text-[#FF9F0A]'}`}>
            {isExecuting ? 'Série em Execução' : 'Posicione-se no Aparelho'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <div className="bg-white/90 dark:bg-[#1C1C1E] border border-black/[0.08] dark:border-white/[0.08] px-3.5 py-1.5 rounded-full text-xs tabular-nums font-bold text-slate-700 dark:text-slate-300 shadow-sm">
            {formatSeconds(totalSessionSeconds)}
          </div>
          <button
            onClick={onExit}
            className="w-8 h-8 rounded-full bg-white/90 dark:bg-[#1C1C1E] border border-black/[0.08] dark:border-white/[0.08] text-slate-500 hover:text-slate-950 dark:text-slate-400 dark:hover:text-white flex items-center justify-center apple-press shadow-sm cursor-pointer"
            title="Sair do treino"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 2. Informações do Exercício Atual (TIGHT SPACING: directly below the header) */}
      <div className="space-y-3 pt-0.5">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Exercício {currentExIndex + 1} de {exercises.length}
            </span>
            <span className="text-xs font-black tabular-nums text-[#30D158]">
              Série {currentSetNumber} de {currentExercise?.default_sets || 4}
            </span>
          </div>

          <h1 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            {currentExercise?.name}
          </h1>

          <div className="flex items-center gap-2 pt-0.5">
            <span className="text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full bg-[#30D158]/15 text-[#30D158] border border-[#30D158]/30">
              {currentExercise?.target_muscle}
            </span>
            <button
              onClick={() => setShowDemo(!showDemo)}
              className="text-[11px] font-semibold text-[#0A84FF] flex items-center gap-1 hover:underline ml-auto cursor-pointer"
            >
              <Info className="w-3.5 h-3.5" />
              <span>{showDemo ? 'Ocultar' : 'Ver Execução'}</span>
            </button>
          </div>
        </div>

        {/* Demonstração visual expansível */}
        {showDemo && currentExercise?.demo_url && (
          <div className="rounded-[22px] overflow-hidden border border-black/[0.08] dark:border-white/[0.08] bg-black shadow-sm animate-fadeIn">
            <img
              src={currentExercise.demo_url}
              alt={currentExercise.name}
              className="w-full h-44 object-cover"
            />
            {currentExercise.instructions && (
              <div className="px-3.5 py-2.5 bg-slate-50 dark:bg-[#2C2C2E] border-t border-black/[0.06] dark:border-white/[0.08] flex items-start gap-2 text-left">
                <Flame className="w-3.5 h-3.5 text-[#FF9F0A] shrink-0 mt-0.5" />
                <p className="text-[11px] leading-relaxed text-slate-700 dark:text-white/80 font-medium">
                  {currentExercise.instructions}
                </p>
              </div>
            )}
          </div>
        )}

        {/* DISPLAY CENTRAL DA ETAPA */}
        {!isExecuting ? (
          /* ETAPA 'prep': Tolerância para chegar no aparelho */
          <div className="p-6 rounded-[28px] bg-white/95 dark:bg-[#1C1C1E] border border-black/[0.06] dark:border-white/[0.08] text-center space-y-4 shadow-md">
            <span className="text-[11px] font-bold text-slate-500 dark:text-white/60 uppercase tracking-wider block">
              Tolerância para Deslocamento
            </span>

            <div className="text-7xl font-extrabold tabular-nums tracking-tighter text-[#30D158] animate-pulse drop-shadow-[0_0_20px_rgba(48,209,88,0.3)]">
              00:{prepTimeLeft < 10 ? `0${prepTimeLeft}` : prepTimeLeft}
            </div>

            <p className="text-xs text-slate-600 dark:text-white/70 max-w-xs mx-auto leading-relaxed">
              Vá para o equipamento, ajuste o banco e posicione a carga de <strong>{activeWeight} kg</strong>.
            </p>

            {/* Ajuste Tátil Ergonômico de Carga antes de começar */}
            <div className="flex items-center justify-center gap-4 py-1">
              <button
                onClick={() => setActiveWeight((w) => Math.max(0, w - 2.5))}
                className="w-13 h-13 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-[#2C2C2E] dark:hover:bg-[#3A3A3C] border border-black/[0.06] dark:border-white/[0.08] text-slate-900 dark:text-white font-black text-2xl flex items-center justify-center active:scale-90 transition cursor-pointer apple-press shadow-sm"
              >
                -
              </button>
              <div className="text-center tabular-nums font-black text-3xl text-slate-900 dark:text-white w-32">
                {activeWeight} <span className="text-sm text-slate-500 dark:text-white/50 font-normal">kg</span>
              </div>
              <button
                onClick={() => setActiveWeight((w) => w + 2.5)}
                className="w-13 h-13 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-[#2C2C2E] dark:hover:bg-[#3A3A3C] border border-black/[0.06] dark:border-white/[0.08] text-slate-900 dark:text-white font-black text-2xl flex items-center justify-center active:scale-90 transition cursor-pointer apple-press shadow-sm"
              >
                +
              </button>
            </div>

            {/* Botão Iniciar Série */}
            <button
              onClick={startExecution}
              className="w-full py-5 px-8 rounded-2xl bg-[#30D158] hover:bg-[#28C846] text-black font-black text-2xl uppercase tracking-wider shadow-lg active:scale-95 transition-all flex items-center justify-center gap-3 cursor-pointer"
            >
              <Play className="w-7 h-7 fill-black" />
              <span>Iniciar Série</span>
            </button>
          </div>
        ) : (
          /* ETAPA 'executing': Série em andamento */
          <div className="p-6 rounded-[28px] bg-white/95 dark:bg-[#1C1C1E] border border-black/[0.06] dark:border-white/[0.08] text-center space-y-4 shadow-md">
            <span className="text-[11px] font-extrabold text-[#30D158] uppercase tracking-wider block">
              Tempo sob Tensão
            </span>

            <div className="text-7xl font-extrabold tabular-nums tracking-tighter text-slate-900 dark:text-white">
              {formatSeconds(executingSeconds)}
            </div>

            {/* Trava dos 15 segundos ou Botão Liberado no Alto e Gigante */}
            {isLocked ? (
              <div className="space-y-3 pt-1">
                <div className="w-full h-3 bg-black/[0.08] dark:bg-white/[0.08] rounded-full overflow-hidden p-0.5 border border-black/[0.06] dark:border-white/[0.1]">
                  <div
                    className="h-full bg-gradient-to-r from-[#30D158] to-[#34C759] rounded-full transition-all duration-1000 shadow-[0_0_10px_#30D158]"
                    style={{ width: `${lockProgress}%` }}
                  />
                </div>

                <div className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-600 dark:text-white/60">
                  <Clock className="w-4 h-4 text-[#30D158] animate-spin" />
                  <span>Cadência controlada • destravando em {15 - executingSeconds}s</span>
                </div>

                {/* Botão Travado */}
                <button
                  disabled
                  className="w-full py-5 px-6 rounded-[24px] font-bold text-base uppercase tracking-wider bg-black/[0.04] dark:bg-white/[0.06] text-slate-400 dark:text-white/40 border border-black/[0.06] dark:border-white/[0.08] cursor-not-allowed opacity-75 flex items-center justify-center gap-2.5"
                >
                  <Lock className="w-5 h-5 text-slate-400 dark:text-white/40" />
                  <span>Executando ({15 - executingSeconds}s restantes)</span>
                </button>
              </div>
            ) : (
              <div className="space-y-3 pt-1">
                {/* BOTÃO FINALIZAR SÉRIE */}
                <button
                  onClick={handleFinishSet}
                  className="w-full py-6 px-8 rounded-2xl font-black text-2xl uppercase tracking-wider bg-[#30D158] hover:bg-[#28C846] text-black shadow-lg active:scale-95 transition-all flex items-center justify-center gap-3 cursor-pointer"
                >
                  <Check className="w-8 h-8 stroke-[3.5]" />
                  <span>FINALIZAR SÉRIE</span>
                </button>

                <div className="flex items-center justify-center gap-1.5 text-xs font-bold text-[#30D158]">
                  <Sparkles className="w-4 h-4 text-[#30D158]" />
                  <span>Série válida! Finalize ao atingir a falha.</span>
                </div>
              </div>
            )}

            {/* Ajuste Tátil Ergonômico de Reps */}
            <div className="flex items-center justify-between p-3.5 rounded-[20px] bg-black/[0.04] dark:bg-[#2C2C2E] border border-black/[0.06] dark:border-white/[0.08] mt-3">
              <span className="text-xs text-slate-700 dark:text-slate-300 font-bold">Reps Realizadas:</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setActiveReps((r) => Math.max(1, r - 1))}
                  className="w-10 h-10 rounded-full bg-slate-200 hover:bg-slate-300 dark:bg-[#3A3A3C] dark:hover:bg-[#48484A] text-slate-800 dark:text-white font-black text-lg active:scale-90 cursor-pointer apple-press flex items-center justify-center shadow-sm"
                >
                  -
                </button>
                <span className="text-xl tabular-nums font-black text-slate-900 dark:text-white w-8 text-center">
                  {activeReps}
                </span>
                <button
                  onClick={() => setActiveReps((r) => r + 1)}
                  className="w-10 h-10 rounded-full bg-slate-200 hover:bg-slate-300 dark:bg-[#3A3A3C] dark:hover:bg-[#48484A] text-slate-800 dark:text-white font-black text-lg active:scale-90 cursor-pointer apple-press flex items-center justify-center shadow-sm"
                >
                  +
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
